import DAO from './DAO.js';
import teamsService from '../services/teamsService';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import dataModel from '../models/DataModel';
import { RecordStates } from '../constants/ReduxConstants.js';

export const team_root = {
  team:
  {
    lead: { additionalFields: ['profile_image'] },
    users: { additionalFields: ['profile_image'] },
  },
};

export default class TeamsDAO extends DAO {

  constructor(store) {
    super(store, 'team');
    this.unsetTeam = this.unsetTeam.bind(this);
    this.clearTeams = this.clearTeams.bind(this);
    this.addTeamUser = this.addTeamUser.bind(this);
    this.removeTeamUser = this.removeTeamUser.bind(this);
  }

  unsetTeam(id) {
    StoreItemProcessing.deleteItemInStore(this.itemType, id);
  }

  clearTeams() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }

  getTeam(teamId, options) {
    if (!options) {
      options = { recordDescriptor: { team: {} } };
    }
    return this._getItem(teamId, teamsService.getTeam.bind(this, teamId, dataModel.companiesDAO.currentCompany.id, options), options);
  }

  handleResponse(team, response, dontQueue, options, errorMessage) {
    if (response.offline && dontQueue) {
      return response;    
    }
    else if (response.offline) {
      this.throwItemError();
    }
    else if (response.error) {
      dataModel.displayHTTPError(errorMessage, response.error, false);
      this.markError(team);
      this.throwItemError();
    }
    else {
      this.normalizeAndSyncToStore(response, options);
      const denormalized = this.mapToReturn(this.findItem(response.data.id), options);
      return denormalized;
    }
  }

  createTeam(team, eventMetaDatam, options) {
    return teamsService.createTeam(team, null, options).then(response => {
      return this.handleResponse(team, response, false, options, 'Creating a team');
    });
  }

  updateTeam(team, options) {
    return teamsService.updateTeam(team, null, options).then(response => {
      return this.handleResponse(team, response, false, options, 'Updating a team');
    });
  }

  deleteTeamLogo(team, options) {
    return teamsService.deleteTeamLogo(team.id, null, options).then(response => {
      return this.handleResponse(team, response, false, options, 'Deleting the team logo');
    });
  }

  deleteTeam(team) {
    this.markDeleted(team);
    return teamsService.deleteTeam(team.id).then(response => {
      return response;
    });
  }

  addTeamUser(team, userId, options) {
    return teamsService.addUserToTeam(userId, team.id, null, options).then(response => {
      return this.handleResponse(team, response, false, options, 'Adding team user');
    });
  }

  removeTeamUser(team, userId, options) {
    return teamsService.deleteUserFromTeam(userId, team.id, null).then(response => {
      return this.handleResponse(team, response, false, options, 'Removing team user');
    });
  }

  addTeamProject(team, projectId, projectManagers, projectTypes, options) {
    return teamsService.addProjectToTeam(projectId, team.id, null, team.markets, projectManagers, projectTypes, options).then(response => {
      return this.handleResponse(team, response, false, options, 'Adding team project');
    });
  }

  removeTeamProject(team, projectId, options) {
    return teamsService.deleteProjectFromTeam(projectId, team.id, null).then(response => {
      return this.handleResponse(team, response, false, options, 'Removing team project');
    });
  }

  bulkAddTeamUsers(team, userIds, options) {
    return teamsService.bulkAddUsersToTeam(team.id, userIds, null, options).then(response => {
      return this.handleResponse(team, response, false, options, 'Adding team users');
    });
  }

  bulkAddTeamProjects(team, payload, options) {
    return teamsService.bulkAddProjectsToTeam(team.id, payload, null, options).then(response => {
      return this.handleResponse(team, response, false, options, 'Adding team projects');
    });
  }

  checkProjectUsage(teamId, projectId, options, eventMetadatum) {
    return teamsService.checkProjectUsage(teamId, projectId, options, eventMetadatum);
  }

  addTeamVendorUsersWithPromise(vendorUsers) {
    const vendorUserPromises =[];
    for(let vendorUser of vendorUsers) {
      vendorUserPromises.push(teamsService.addVendorUserToTeam(vendorUser.userId, vendorUser.teamId, vendorUser.companyId));
    }
    return Promise.all(vendorUserPromises).then(results => {
      return results;
    });
  }

  removeTeamVendorUsersWithPromise(vendorUsers) {
    const vendorUserPromises =[];
    for(let vendorUser of vendorUsers) {
      vendorUserPromises.push(teamsService.deleteVendorUserFromTeam(vendorUser.userId, vendorUser.teamId, vendorUser.companyId));
    }
    return Promise.all(vendorUserPromises).then(results => {
      return results;
    });
  }

  clearState(options) {
    this.clearMyState(this.stateTree.new_teams, options.toIgnore.teams, this.unsetTeam, this.clearTeams, options);
  }

  checkTeamsCollection(user_id) {
    let collectionIds = dataModel.collectionsDAO.getCollectionsByType('TEAM');
    for (let collectionId of collectionIds) {
      let collection = dataModel.collectionsDAO.findCollection(collectionId);  

      if (collection && !collection.ids.find(id => {return id == user_id;})){
        collection._recordState = RecordStates.NEEDS_SYNC;
        dataModel.collectionsDAO.setCollection(collectionId, collection, 'TEAM');
      }
    }
  }

  handleActionCableAndGetRequest(acData) {
    let team = this.findItem(acData.team_id);
    if (team) {
      if (acData.is_attribute_update) {
        this.markNeedsAttributeSync(team);
      }
      else {
        this.markNeedsSync(team);
      }
    }
    this.checkTeamsCollection(acData.team_id);
    switch (acData.type) {
    case 'newTeam':
    case 'updateTeam':
      return this.getTeam.bind(this);
    case 'deleteTeam':
      this.unsetTeam(acData.team_id);
      return team;
    default:
    }

  }
}



// WEBPACK FOOTER //
// ./src/dao/TeamsDAO.js